import { Badge, Box, Button, Container, Group, Image, Loader, SimpleGrid, Stack, Table, Text, Title } from '@mantine/core'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CommunityGem, DayOfWeek } from '../models/CommunityGem'
import { getGemById } from '../data/communityGems.service'

const DAY_NAMES: DayOfWeek[] = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const formatTime = (time: string | null): string => {
  if (!time) return ''
  const [hours, minutes] = time.split(':').map(Number)
  const period = hours >= 12 ? 'PM' : 'AM'
  const displayHours = hours % 12 || 12
  return `${displayHours}:${String(minutes).padStart(2, '0')} ${period}`
}

function CommunityGemDetailPage() {
  const { id } = useParams<{ id: string }>()
  const [gem, setGem] = useState<CommunityGem | null>(null)
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)

  useEffect(() => {
    if (!id) return
    getGemById(Number(id))
      .then(setGem)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) return (
    <Box style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
      <Loader />
    </Box>
  )
  if (notFound || !gem) return (
    <Container py='xl'>
      <Text ta='center'>Gem not found.</Text>
    </Container>
  )

  const sortedHours = [...gem.hours].sort(
    (a, b) => DAY_NAMES.indexOf(a.dayOfWeek) - DAY_NAMES.indexOf(b.dayOfWeek)
  )

  return (
    <Container size='lg' py='xl'>
      {gem.photos[0] && (
        <Image src={gem.photos[0].imageUrl} height={300} radius='md' mb='xl' alt={gem.name} />
      )}

      <SimpleGrid cols={{ base: 1, md: 2 }} spacing='xl'>
        <Stack>
          <Title>{gem.name}</Title>
          <Text c='dimmed'>
            {[gem.address, gem.city, gem.state, gem.zipCode].filter(Boolean).join(', ') || '—'}
          </Text>
          <Group gap='xs'>
            {gem.tags.map(t => (
              <Badge key={t.id} color='green' variant='light'>{t.name}</Badge>
            ))}
          </Group>
          {gem.description && <Text>{gem.description}</Text>}
          <Group gap='xs' mt='sm'>
            {gem.websiteUrl && (
              <Button component='a' href={gem.websiteUrl} target='_blank' rel='noopener noreferrer' size='sm'>
                Website
              </Button>
            )}
            {gem.instagramUrl && (
              <Button component='a' href={gem.instagramUrl} target='_blank' rel='noopener noreferrer' variant='outline' size='sm'>
                Instagram
              </Button>
            )}
            {gem.facebookUrl && (
              <Button component='a' href={gem.facebookUrl} target='_blank' rel='noopener noreferrer' variant='outline' size='sm'>
                Facebook
              </Button>
            )}
          </Group>
          {gem.locationId && (
            <Box
              p='sm'
              style={{
                background: '#f0f7f4',
                borderLeft: '4px solid #16503a',
                borderRadius: 4,
              }}
            >
              <Text size='sm' c='dark'>✦ This is a HiddenGemz partner location.</Text>
            </Box>
          )}
        </Stack>

        {sortedHours.length > 0 && (
          <Stack>
            <Title order={4}>Hours</Title>
            <Table>
              <Table.Tbody>
                {sortedHours.map(h => (
                  <Table.Tr key={h.dayOfWeek}>
                    <Table.Td fw={500}>{h.dayOfWeek}</Table.Td>
                    <Table.Td c={h.isClosed ? 'red' : undefined}>
                      {h.isClosed ? 'Closed' : `${formatTime(h.openTime)} – ${formatTime(h.closeTime)}`}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Stack>
        )}
      </SimpleGrid>

      {gem.photos.length > 1 && (
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing='sm' mt='xl'>
          {gem.photos.slice(1).map(p => (
            <Image key={p.id} src={p.imageUrl} height={120} radius='sm' alt='' />
          ))}
        </SimpleGrid>
      )}
    </Container>
  )
}

export default CommunityGemDetailPage
